import React, { useState, useRef } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Edit, Trash2, Plus, Eye, Lock, Upload, Image, X } from "lucide-react";
import CKEditorComponent from "@/components/CKEditorComponent";

// Récupérer le mot de passe depuis les variables d'environnement
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || "Fred2025!Amsweb";

export default function AdminArticles() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  
  const [editingArticle, setEditingArticle] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    category: "MLM Crypto",
    excerpt: "",
    content: "",
    image_url: "",
    read_time: 10,
    published: true
  });

  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileInputRef = useRef(null);

  const queryClient = useQueryClient();

  // Upload image function
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError("Type de fichier non autorisé. Utilisez JPEG, PNG, GIF ou WEBP.");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError("Fichier trop volumineux. Maximum 5 Mo.");
      return;
    }

    setIsUploading(true);
    setUploadError("");

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/image`, {
        method: 'POST',
        body: formDataUpload
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'upload');
      }

      const data = await response.json();
      
      // Construct full URL for the image using /api/uploads/ route
      const fullImageUrl = `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${data.filename}`;
      setFormData({...formData, image_url: fullImageUrl});
      
    } catch (error) {
      setUploadError("Erreur lors de l'upload de l'image. Réessayez.");
      console.error('Upload error:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const { data: articles = [], isLoading } = useQuery({
    queryKey: ['articles-admin'],
    queryFn: () => base44.entities.Article.list('-created_date'),
    enabled: isAuthenticated
  });

  // Login handler
  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setLoginError("");
      setPassword("");
    } else {
      setLoginError("Mot de passe incorrect");
    }
  };

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.Article.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['articles-admin']);
      alert("Article supprimé avec succès !");
    }
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.Article.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['articles-admin']);
      setIsCreating(false);
      setFormData({
        title: "",
        slug: "",
        category: "MLM Crypto",
        excerpt: "",
        content: "",
        image_url: "",
        read_time: 10,
        published: true
      });
      alert("Article créé avec succès !");
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => {
      return fetch(`${process.env.REACT_APP_BACKEND_URL}/api/article/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      }).then(res => res.json());
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['articles-admin']);
      setEditingArticle(null);
      setFormData({
        title: "",
        slug: "",
        category: "MLM Crypto",
        excerpt: "",
        content: "",
        image_url: "",
        read_time: 10,
        published: true
      });
      alert("Article mis à jour avec succès !");
    }
  });

  const handleEdit = (article) => {
    setEditingArticle(article);
    setFormData({
      title: article.title,
      slug: article.slug,
      category: article.category,
      excerpt: article.excerpt,
      content: article.content,
      image_url: article.image_url || "",
      read_time: article.read_time || 10,
      published: article.published
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (article) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer "${article.title}" ?`)) {
      deleteMutation.mutate(article.id);
    }
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingArticle(null);
    setFormData({
      title: "",
      slug: "",
      category: "MLM Crypto",
      excerpt: "",
      content: "",
      image_url: "",
      read_time: 10,
      published: true
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (isCreating) {
      // Créer un nouvel article
      createMutation.mutate(formData);
    } else if (editingArticle) {
      // Modifier un article existant
      updateMutation.mutate({
        id: editingArticle.id,
        data: formData
      });
    }
  };

  const handleCancel = () => {
    setEditingArticle(null);
    setIsCreating(false);
    setFormData({
      title: "",
      slug: "",
      category: "MLM Crypto",
      excerpt: "",
      content: "",
      image_url: "",
      read_time: 10,
      published: true
    });
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 to-purple-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-2xl">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl">Administration Articles</CardTitle>
            <p className="text-slate-600">Accès réservé à Fred d'Amsweb</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="password">Mot de passe</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Entrez le mot de passe admin"
                  className="mt-2"
                  required
                />
              </div>
              {loginError && (
                <p className="text-red-600 text-sm">{loginError}</p>
              )}
              <Button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-purple-600">
                Se connecter
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin Interface
  return (
    <div className="min-h-screen bg-slate-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">
              Gestion des Articles
            </h1>
            <p className="text-slate-600">
              {articles.length} article{articles.length > 1 ? 's' : ''} au total
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={handleCreate}
              className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Créer un article
            </Button>
            <Button
              onClick={() => setIsAuthenticated(false)}
              variant="outline"
            >
              Déconnexion
            </Button>
          </div>
        </div>

        {/* Create/Edit Form */}
        {(isCreating || editingArticle) && (
          <Card className="mb-8 border-2 border-blue-500">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
              <CardTitle>
                {isCreating ? "Créer un nouvel article" : "Modifier l'article"}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="title">Titre *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      required
                      className="mt-2"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="slug">Slug (URL) *</Label>
                    <Input
                      id="slug"
                      value={formData.slug}
                      onChange={(e) => setFormData({...formData, slug: e.target.value})}
                      required
                      className="mt-2"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="category">Catégorie *</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({...formData, category: value})}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MLM Crypto">MLM Crypto</SelectItem>
                        <SelectItem value="DeFi">DeFi</SelectItem>
                        <SelectItem value="Trading">Trading</SelectItem>
                        <SelectItem value="Investissement">Investissement</SelectItem>
                        <SelectItem value="Formation">Formation</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="read_time">Temps de lecture (min)</Label>
                    <Input
                      id="read_time"
                      type="number"
                      value={formData.read_time}
                      onChange={(e) => setFormData({...formData, read_time: parseInt(e.target.value)})}
                      className="mt-2"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="image_url">Image de l'article</Label>
                  <div className="mt-2 space-y-3">
                    {/* Preview de l'image actuelle */}
                    {formData.image_url && (
                      <div className="relative inline-block">
                        <img 
                          src={formData.image_url} 
                          alt="Preview" 
                          className="w-48 h-32 object-cover rounded-lg border"
                        />
                        <button
                          type="button"
                          onClick={() => setFormData({...formData, image_url: ""})}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    
                    {/* Zone d'upload */}
                    <div className="flex flex-col gap-3">
                      <div className="flex gap-3 items-center">
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleImageUpload}
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          className="hidden"
                        />
                        <Button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isUploading}
                          variant="outline"
                          className="flex items-center gap-2"
                        >
                          {isUploading ? (
                            <>
                              <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                              Upload en cours...
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4" />
                              Uploader une image
                            </>
                          )}
                        </Button>
                        <span className="text-sm text-slate-500">ou</span>
                        <Input
                          id="image_url"
                          value={formData.image_url}
                          onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                          placeholder="Coller une URL d'image..."
                          className="flex-1"
                        />
                      </div>
                      
                      {uploadError && (
                        <p className="text-sm text-red-500">{uploadError}</p>
                      )}
                      
                      <p className="text-xs text-slate-500">
                        📷 Formats acceptés : JPEG, PNG, GIF, WEBP (max 5 Mo). L'image sera hébergée sur votre domaine.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="excerpt">Résumé (excerpt) *</Label>
                  <Textarea
                    id="excerpt"
                    value={formData.excerpt}
                    onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                    rows={3}
                    required
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="content">Contenu complet *</Label>
                  <div className="mt-2">
                    <CKEditorComponent
                      value={formData.content}
                      onChange={(html) => setFormData({...formData, content: html})}
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    💡 Éditeur CKEditor 5 - Comme Word ou Google Docs. Appuyez sur Enter pour créer des paragraphes espacés.
                  </p>
                </div>

                <div className="flex items-center gap-4">
                  <input
                    type="checkbox"
                    id="published"
                    checked={formData.published}
                    onChange={(e) => setFormData({...formData, published: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="published">Article publié (visible sur le blog)</Label>
                </div>

                <div className="flex gap-4">
                  <Button
                    type="submit"
                    disabled={createMutation.isPending || updateMutation.isPending}
                    className="bg-gradient-to-r from-green-600 to-emerald-600"
                  >
                    {createMutation.isPending || updateMutation.isPending 
                      ? "Enregistrement..." 
                      : isCreating 
                        ? "Créer l'article" 
                        : "Enregistrer les modifications"
                    }
                  </Button>
                  <Button
                    type="button"
                    onClick={handleCancel}
                    variant="outline"
                  >
                    Annuler
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Articles List */}
        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-slate-600">Chargement des articles...</p>
          </div>
        ) : (
          <div className="grid gap-6">
            {articles.map((article) => (
              <Card key={article.id} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    {article.image_url && (
                      <img
                        src={article.image_url}
                        alt={article.title}
                        className="w-32 h-24 object-cover rounded-lg"
                      />
                    )}
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-xl font-bold text-slate-900">
                              {article.title}
                            </h3>
                            <Badge variant={article.published ? "default" : "secondary"}>
                              {article.published ? "Publié" : "Brouillon"}
                            </Badge>
                            <Badge>{article.category}</Badge>
                          </div>
                          <p className="text-slate-600 text-sm line-clamp-2 mb-2">
                            {article.excerpt}
                          </p>
                          <div className="flex items-center gap-4 text-xs text-slate-500">
                            <span>📖 {article.read_time} min</span>
                            <span>🔗 /{article.slug}</span>
                            <span>📅 {new Date(article.created_date).toLocaleDateString('fr-FR')}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        onClick={() => handleEdit(article)}
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Modifier
                      </Button>
                      <a
                        href={`/blog/article?slug=${article.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Voir
                        </Button>
                      </a>
                      <Button
                        onClick={() => handleDelete(article)}
                        size="sm"
                        variant="destructive"
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
